// Do not allow cheats in production
import {
  action,
  asyncAction,
  renderTemplate,
  renderTemplatesWithAssets,
  SB,
} from '@play-co/replicant';
import { TemplateRendererAPI } from '@play-co/replicant';
import * as Fate from '../fate';
import * as Inventory from '../inventory';
import * as Gifts from '../gifts';
import * as Level from '../level';
import { Milestone, MilestoneEvolution } from '../level/level.ruleset';
import { PetAttribute, PetEvolutionStage, PetStatusEffect } from '../pet';
import * as Pet from '../pet';
import {
  nextSpeciesWeights,
  PetSpeciesID,
  petSpeciesTuple,
} from '../pet/pet.ruleset';
import * as Tutorial from '../tutorial';
import { TutorialStep, TutorialSteps } from '../tutorial/tutorial.ruleset';
import {
  ChatbotFlexMessageArgs,
  getChatbotFlexMessage,
} from '../chatbot/flex/utils';
import * as Duo from '../duo';
import {
  dynamicAB,
  DynamicABTestBucketID,
  DynamicABTestID,
} from '../ruleset/ab';
import { type MutableState, stateSchema } from '../State';
import { assertNever } from '../utils';
import { duration } from '../utils/duration';
import createActions, { SyncActionAPI } from '../config/actions/createActions';
import {
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import { Feature } from '../analytics';
import { getLocale } from '../getters/user';
import { addAccessoryItem } from '../accessory/accessory.modifiers';
import { getAvailableAccessories } from '../accessory/accessory.getters';
import { tiersAccessoryItemsMap } from '../accessory/accessory.ruleset';
import { getRandomItemFromArray } from '../utils/random';
import { ChatbotGroup } from '../chatbot/chatbot.ruleset';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getChatbotMessageChannel } from '../chatbot/chatbot.getters';
import { substituteTokens } from '../petChatbot/petChatbot.scheduledActions';
import { PetChatTime } from '../petChatbot';
import * as PetChatbot from '../petChatbot';
import { getPetSpeciesBucket } from '../pet/pet.getters';
import {
  clearMatch2Timestamp,
  grantMatch2Rewards,
  setMatch2Timestamp,
} from '../match2/match2.modifiers';
import * as match2FrenzyModifiers from '../match2/frenzy/match2-frenzy.modifiers';
import { getMatch2FrenzyIntroTimestampKey } from '../match2/match2.ruleset';
import {
  getSharedStateActionsAPI,
  getSharedStateAsyncActionsAPI,
} from '../config/actions/createSharedStatesActions';
import { TeamProfile } from '../team/team.schema';
import {
  inviteChallengeWallpapers,
  successInviteRewardGems,
} from '../social/social.ruleset';
import { getMakeHappyCooldownTimestampKey } from '../match2/match2.ruleset';
import { updateRaceBotsOnGameEnd } from '../match2/races/races.bots';
import { getActiveRace } from '../match2/races/races.getters';
import { ProductId } from '../iap';
import * as Iap from '../iap';
import { petImageGreetingsResponse } from '../petChatbot/petChatbot.greetings';
import { FlexMessagesTemplate } from '../chatbot/flex';
import { decorationCheats } from '../decoration/decoration.cheats';
import { friendsCheats } from '../friends/friends.cheats';
import { deriveJankenOutcome } from '../janken/janken.utils';
import { jankenMoves, jankenOutcomes } from '../janken/janken.ruleset';
import { createJankenPendingResult } from '../janken/janken.modifiers';
import { ResurrectionCreativeVariant } from '../chatbot/flex/resurrect';
import { guestLineLoginReward } from '../guest/guest.ruleset';

const CHEAT_MARKETING_EVENT_ID = 'cheat-marketing-reward';

/**
 * Resets all state used by the centralized launch promo block so launch
 * dialogs can replay: the promo group state plus per-feature seen locks
 * that each item's `isAvailable` check reads.
 */
function resetPromoLaunchLocks(state: MutableState): void {
  state.promo = {};
  state.promoGroups = {};

  // Frenzy: clear intro seen timestamp
  clearMatch2Timestamp(state, getMatch2FrenzyIntroTimestampKey());
  // Super Lollipop: clear last seen timestamp
  if (state.superLollipop) {
    state.superLollipop.lastSeenAt = 0;
  }
  // Streak Quest: clear intro seen flag
  if (state.streak) {
    state.streak.introSeen = false;
  }
  // Treasure Express: clear per-event seen entries
  if (state.treasureExpress) {
    state.treasureExpress.entries = {};
  }
  // Buddies: clear event seenAt so info popup re-shows,
  // and reset lastSeenPartySize so unseen joiners re-appear
  const buddiesEvents = state.bakingBuddies?.events;
  if (buddiesEvents) {
    for (const eventId of Object.keys(buddiesEvents)) {
      const event = buddiesEvents[eventId];
      if (event) {
        event.seenAt = 0;
        event.lastSeenPartySize = 0;
      }
    }
  }
}

export default createActions({
  cheats_asyncActionAPI: asyncAction(
    async (_, args: Record<string, any>, api) => {
      const returns: Record<string, any> = {};

      for (const key in args) {
        const subPath = key.split('.');
        let curr: any = api;
        for (const p of subPath) {
          if (curr[p] === undefined) {
            returns[key] = `Invalid path: ${key} -> ${p} is undefined`;
            break;
          }
          curr = curr[p];
        }
        if (!curr || typeof curr !== 'function') continue;
        returns[key] = await curr(args[key]);
      }

      return returns;
    },
  ),

  cheats_createTeams: asyncAction(
    async (
      state,
      teams: Array<{ profile: Partial<TeamProfile>; members: string[] }>,
      rawApi,
    ) => {
      const api = getSharedStateAsyncActionsAPI(rawApi);

      const ids: string[] = [];
      for (const team of teams) {
        const id = api.sharedStates.team.create();
        api.sharedStates.team.postMessage.updateTeamProfile(id, {
          ...team.profile,
          timestamp: api.date.now(),
        });

        team.members.forEach((userId) => {
          api.sharedStates.team.postMessage.joinTeam(id, {
            timestamp: api.date.now(),
            userId,
          });
        });

        ids.push(id);
      }

      return ids;
    },
  ),

  cheats_postScoreToCurrentTeamEvent: asyncAction(
    async (state, score: number, rawApi) => {
      // Only if currently in a team.
      if (!state.team.currentTeamId) return;

      const api = getSharedStateAsyncActionsAPI(rawApi);
      api.sharedStates.team.postMessage.postEventScore(
        state.team.currentTeamId,
        {
          timestamp: api.date.now(),
          score,
        },
      );
    },
  ),

  cheats_receiveDuoInvitation: action(
    (state, args: { userId: string }, api) => {
      state.duo.invitations.received.push({
        timestamp: api.date.now(),
        playerId: args.userId,
        careItemId:
          api.math.random() > 0.5 ? 'i_vowsCoffee' : 'i_ultimateVowsCoffee',
        status: 'pending',
        pet: { species: 'giraffe', age: 42, name: 'Cheater' },
        lastReminder: 0,
      });

      state.duo.lastReceivedInvitation = {
        timestamp: api.date.now(),
        playerId: args.userId,
        name: 'Cheater',
      };
    },
  ),

  cheats_receiveDuoResponse: action(
    (
      state,
      { accepted, userId }: { accepted: boolean; userId: string },
      api,
    ) => {
      const careItemId =
        api.math.random() > 0.5 ? 'i_vowsCoffee' : 'i_ultimateVowsCoffee';

      const { species } = Pet.getters.getCurrentEvolution(state)!;

      state.duo.invitations.sent.push({
        timestamp: api.date.now(),
        playerId: userId,
        careItemId,
        status: accepted ? 'accepted' : 'cancelled',
        message: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        lastReminder: 0,
        pet: { species, age: 42, name: 'Cheater' },
      });

      state.duo.lastResponse = {
        timestamp: api.date.now(),
        playerId: userId,
        accepted,
        name: 'Cheater',
      };

      if (accepted) {
        state.duo.debut = {
          rewardClaimed: false,
          species: Duo.getters.getNewSpecies(
            [
              nextSpeciesWeights[getPetSpeciesBucket(state)]['giraffe'],
              nextSpeciesWeights[getPetSpeciesBucket(state)]['whale'],
            ],
            api.math.random,
          ),
          careItemId,
          inviterId: userId,
          inviteeId: state.id,
        };
      }
    },
  ),

  cheat_skipTutorial: action((state, _: void, api) => {
    // Complete all tutorial steps.
    TutorialSteps.forEach((step) => {
      Tutorial.modifiers.setTutorialStepComplete(state, step);
    });

    // Unlock visit friend.
    state.petRaised++;

    // Update level to at least 3
    state.match2.level = Math.max(3, state.match2.level);

    // Check if user has a pet, if not create one
    const currentSpecies = Pet.getters.getEquippedPetSpecies(state);

    if (!currentSpecies) {
      Pet.modifiers.createFirstPet(state, api.date.now(), () =>
        api.math.random(),
      );
    }
  }),

  /**
   * Makes the player visitable: friend visit searches drop players whose
   * `offlineUpdateAt` is older than 7 days, treating them as dead accounts.
   */
  cheat_refreshOfflineUpdateAt: action((state, _: void, api) => {
    state.offlineUpdateAt = api.date.now();
  }),

  cheat_setChatbotGroup: action(
    (state, group: ChatbotGroup | undefined, api) => {
      state.chatbot.group = group;
    },
  ),

  cheat_addXP: action(
    (state, args: { milestone: Milestone; petStage: MilestoneEvolution }) => {
      Level.modifiers.incrementXp(state, args.milestone, args.petStage);
    },
  ),

  cheat_addCoins: action((state, args: { amount: number }) => {
    const value = Math.max(0, state.inventory.points + args.amount);
    state.inventory.points = value;
  }),

  cheat_addGems: action((state, args: { amount: number }) => {
    const value = Math.max(0, state.inventory.premium.free + args.amount);

    state.inventory.premium.free = value;

    if (value === 0) {
      const value = Math.max(0, state.inventory.premium.paid + args.amount);

      state.inventory.premium.paid = value;
    }
  }),

  cheat_addStars: action((state, args: { amount: number }) => {
    const value = Math.max(0, state.inventory.stars + args.amount);
    state.inventory.stars = value;
  }),

  cheat_purchase: action((state, args: { productId: ProductId }, api) => {
    // Skip reporting cheats purchases.
    api.sendAnalyticsEvents = () => void 0;

    Iap.modifiers.fulfillPurchase(
      state,
      args.productId,
      api.date.now().toString(),
      api,
    );
  }),

  cheat_addEnergy: action((state, args: { amount: number }, api) => {
    if (args.amount < 0) {
      Inventory.modifiers.consumeEnergy(
        state,
        -args.amount,
        api.date.now(),
        {
          feature: 'cheat',
        },
        api,
      );
      return;
    }
    const value = Math.max(0, (state.inventory.energy += args.amount));

    state.inventory.energy = value;
  }),

  cheat_setAttribute: action(
    (state, args: { attribute: PetAttribute; amount: number }, api) => {
      const now = api.date.now();
      let value = Pet.getters.getAttributeValue(state, args.attribute, now);

      value += args.amount;

      Pet.modifiers.setAttribute(state, args.attribute, value, now);

      console.warn(`Attribute ${args.attribute} set to ${value}%`);
    },
  ),

  cheat_setEffect: action((state, args: { effect: PetStatusEffect }, api) => {
    const now = api.date.now();
    const currentValue = Fate.getters.hasPermanentStatusEffect(
      state,
      args.effect,
      now,
    );

    Fate.modifiers.resolveStatusEffect(state, args.effect, !currentValue, now);

    Fate.modifiers.update(state, now);

    console.warn(`Pet status effect ${args.effect} set to ${!currentValue}%`);
  }),

  cheat_setHadVisitor: action((state, _, api) => {
    const now = api.date.now();
    const hadVisitor = Fate.getters.hasPermanentStatusEffect(
      state,
      'hadVisitor',
      now,
    );
    Fate.modifiers.resolveStatusEffect(state, 'hadVisitor', !hadVisitor, now);
    Fate.modifiers.update(state, now);

    if (!hadVisitor) {
      const profile = state.publicProfile ?? state.profile;
      const { name } = profile!;
      state.visit.lastVisitorName = name;
      state.visit.lastVisitAt = now - duration({ hours: 1 });
    }
  }),

  cheat_setSpecies: action(
    (state, args: { stage: PetEvolutionStage; species: PetSpeciesID }, api) => {
      const len = state.pet.evolutions.length;

      state.pet.evolutions[len - 1].species = args.species;
      state.pet.evolutions[len - 1].stage = args.stage;
    },
  ),

  cheat_setVisitKickedOut: action((state) => {
    if (state.visit.visitSession) {
      state.visit.visitSession.isKickedOut = true;
    }
  }),

  cheat_reset: action((state, args: void, api) => {
    // Out with previous state.
    for (const key in state) {
      if (key === 'iapComponent') continue;
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        delete (state as any)[key];
      }
    }

    // In with the default state.
    Object.assign(state, stateSchema.getDefault());

    // Nuke metadata
    api.nukeUserMetainfo();
  }),

  cheat_resetTutorial: action((state, _: void, api) => {
    const currentAbTests = Object.assign({}, state.abTests);
    const platformStorage = Object.assign({}, state.platformStorage);
    const dynamicABTests = Object.assign({}, state.ruleset.abTests);

    // Out with previous state
    for (const key in state) {
      if (key === 'iapComponent') continue;
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        delete (state as any)[key];
      }
    }

    platformStorage.entry = {
      count: 0,
      first: 0,
      last: 0,
      lastAdEntry: 0,
      lastNativeAndroidEntry: 0,
      lastNativeIOSEntry: 0,
      lastResurrected: 0,
    };

    // In with the default state.
    Object.assign(
      state,
      stateSchema.getDefault(),
      { abTests: currentAbTests },
      { platformStorage },
    );

    // Nuke metadata
    api.nukeUserMetainfo();

    // Assign dynamic AB tests.
    Object.entries(dynamicABTests).forEach(([key, value]) => {
      if (!dynamicAB[key as keyof typeof dynamicABTests]) return;
      api.abTests.assign(key as any, value.bucketId);
    });
  }),

  cheat_setClockOffset: action((state, args: { offset: number }, api) => {
    api.setClockOffset(args.offset);
  }),

  cheat_getClockOffset: action((state, args: void, api) => {
    return api.getClockOffset();
  }),

  cheats_assignABTestManually: action(
    (
      state,
      args: { testId: DynamicABTestID; bucketId: DynamicABTestBucketID },
      api,
    ) => {
      api.abTests.assign(args.testId, args.bucketId);
    },
  ),

  cheats_resetABTestManualAssignment: action(
    (state, args: { testId: DynamicABTestID }, api) => {
      if (api.abTests.getBucketID(args.testId)) {
        api.abTests.unassign(args.testId);
      }
    },
  ),
  cheats_receive_gift: action((state, args: { userId: string }, api) => {
    const reward = { bPoints: 100 };
    state.gifts.received[args.userId] = {
      ...reward,
      sentAt: api.date.now(),
    };
  }),
  cheats_reset_gifts: action((state, _: void, api) => {
    state.gifts.sent = {};
    state.gifts.collected = { gems: 0, collectedAt: 0 };
  }),
  /**
   * Set up a gift cap scenario (#12235). Replaces received and sent gifts.
   * Senders get gem gifts one minute apart, the first one is the oldest.
   */
  cheats_setGiftCapScenario: action(
    (
      state,
      args: {
        senderIds: string[];
        sentToIds: string[];
        collectedGems: number;
        collectedDaysAgo: number;
      },
      api,
    ) => {
      const now = api.date.now();

      state.gifts.received = {};
      args.senderIds.forEach((senderId, index) => {
        state.gifts.received[senderId] = {
          gems: Gifts.ruleset.gemGiftAmount,
          sentAt: now - duration({ minutes: args.senderIds.length - index }),
        };
      });

      state.gifts.sent = {};
      args.sentToIds.forEach((targetId) => {
        state.gifts.sent[targetId] = { sentAt: now };
      });

      state.gifts.collected = {
        gems: args.collectedGems,
        collectedAt: now - duration({ days: args.collectedDaysAgo }),
      };
    },
  ),
  /** Receive new gem gifts from the given friends now (#12235) */
  cheats_receiveGemGifts: action(
    (state, args: { senderIds: string[] }, api) => {
      args.senderIds.forEach((senderId) => {
        state.gifts.received[senderId] = {
          gems: Gifts.ruleset.gemGiftAmount,
          sentAt: api.date.now(),
        };
      });
    },
  ),
  cheats_reset_invites: action((state, _: void, api) => {
    state.social.invites = {};
  }),
  cheats_reset_marketing: action((state, _: void, api) => {
    state.marketing = {};
  }),
  cheats_reset_resubscription: action((state, _: void, api) => {
    delete state.chatbot.resubscriptionState;
    delete state.chatbot.resubscriptionPromptTimestamp;
  }),
  cheat_send_chatbot_message: action(
    (
      state,
      args: ChatbotFlexMessageArgs & {
        channel: 'oa' | 'push' | null;
        debugAssets: Record<string, string>;
      },
      api,
    ) => {
      // Send flex message as usual
      const message = getChatbotFlexMessage(args);
      deliverChatbotMessage(state, api, {
        receiverId: api.getUserID(),
        message,
        channel: args.channel,
      });

      // Fake template api here
      const templateRendering: TemplateRendererAPI<any> = {
        getAssetPath: (x) => args.debugAssets[x as string],
        getSenderID: () => state.id,
        getReceiverID: () => state.id,
        getUserAssetUrl: (key: string) => key,
      };

      // Use face API render finale template
      if (!message.renderers.line) {
        throw new Error('No line renderer found');
      }

      const finalTemplate = message.renderers.line({
        args: args.data as never, // TODO: Fix types here.
        payload: args.payload,
        api: templateRendering,
      })!;

      // Return sent template back to client
      return finalTemplate;
    },
  ),
  cheat_send_pet_greeting_message: action(
    (
      state,
      args: {
        channel: 'oa' | 'push' | null;
        time: PetChatTime;
      },
      api,
    ) => {
      const { getCurrentEvolution, getPetName } = Pet.getters;
      const species = getCurrentEvolution(state)!.species;
      const messages = PetChatbot.ruleset.petSpeciesMessages[species];
      const messageItem = getRandomItemFromArray(
        messages.filter(({ time }) => time === args.time),
        api.math.random,
      );
      const messageTemplate =
        getLocale(state) === 'en'
          ? messageItem.text_1_en
          : messageItem.text_1_jp;
      let message = substituteTokens(messageTemplate, {
        pet_name: state.pet.name,
        player_name: state.profile!.name!,
      });

      // Replace newlines with spaces for push notifications
      const { channel } = args;
      if (channel === 'push') {
        message = message.replace(/\n\n/g, ' ');
      }

      deliverChatbotMessage(state, api, {
        receiverId: api.getUserID(),
        channel,
        message: chatbotMessageTemplates.greeting({
          args: {
            species,
            petName: getPetName(state),
            message,
            locale: getLocale(state),
          },
          payload: generateReplicantChatbotPayload({
            feature: Feature.GREETING._,
            $subFeature: args.time,
          }),
        }),
      });
    },
  ),

  cheat_send_pet_greeting_image: action(
    (state, argsTime: 'morning' | 'lunch' | 'evening', api) => {
      deliverChatbotMessage(state, api, {
        receiverId: state.id,
        channel: 'oa',
        message: petImageGreetingsResponse(
          state,
          {
            type: argsTime,
            channel: 'oa',
            random: api.math.random,
          },
          api,
          api.date.now(),
        ),
      });
    },
  ),

  cheat_send_chatbot_wallpaper_message: action((state, _, api) => {
    inviteChallengeWallpapers.forEach((wallpaper) => {
      api.chatbot.sendMessage(
        api.getUserID(),
        chatbotMessageTemplates[`wallpaper_${wallpaper}_1080`]({
          args: {},
          payload: generateReplicantChatbotPayload({
            feature: Feature.INVITE._,
            $subFeature: Feature.INVITE.WALLPAPER_REWARD,
          }),
        }),
        { forceChannel: 'chatbot' },
      );

      api.chatbot.sendMessage(
        api.getUserID(),
        chatbotMessageTemplates[`wallpaper_${wallpaper}_1170`]({
          args: {},
          payload: generateReplicantChatbotPayload({
            feature: Feature.INVITE._,
            $subFeature: Feature.INVITE.WALLPAPER_REWARD,
          }),
        }),
      );
    });
  }),

  cheat_setNativeAppInstalled: action(
    (
      state,
      args: { platform: 'google' | 'apple'; isInstalled: boolean },
      api,
    ) => {
      const nativeLaunchTime = args.isInstalled ? api.date.now() : 0;
      switch (args.platform) {
        case 'apple':
          state.native.appleLastLaunched = nativeLaunchTime;
          return;
        case 'google':
          state.native.googleLastLaunched = nativeLaunchTime;
          return;
        default:
          assertNever(args.platform);
      }
    },
  ),

  cheat_addAttributeEffect: action(
    (
      state,
      args: { decay: number; duration: number; attribute: PetAttribute },
      api,
    ) => {
      const { decay, duration, attribute } = args;
      Pet.modifiers.addAttributeEffectDuration(
        state,
        attribute,
        duration,
        decay,
        api.date.now(),
      );

      Fate.modifiers.update(state, api.date.now());
    },
  ),

  cheat_resetFriendsList: action((state) => {
    state.buruFriends = {};
  }),

  cheat_resetFriendshipRequests: action((state) => {
    state.friendshipRequests = {};
  }),

  cheat_sendWeeklyLeaderboardNotification: action(
    (state, args: { type: '28' | '4' }, api) => {
      api.scheduledActions.schedule.sendWeeklyLeaderboardMessage({
        delayInMS: 0,
        args: {
          type: `${args.type}h`,
        },
        notificationId: `weeklyLeaderboard${args.type}hCheats`,
      });
    },
  ),

  cheat_sendMiniappWelcome: action((state, _, api) => {
    api.scheduledActions.schedule.sendMiniappWelcome({
      args: {},
      delayInMS: 0,
      targetUserId: state.id,
      notificationId: 'miniappWelcome',
    });
  }),

  cheat_send_chatbot_reengagement_message: action(
    (state, args: { day: '7' | '14' | '28' }, api) => {
      const { day } = args;
      const petName = Pet.getters.getPetName(state);
      const species = Pet.getters.getCurrentEvolution(state)!.species;
      deliverChatbotMessage(state, api, {
        receiverId: api.getUserID(),
        channel: getChatbotMessageChannel(
          state,
          { name: 'reengagement' },
          api.date.now(),
        ),
        message: chatbotMessageTemplates.reengagement({
          args: { day, petName, species, locale: getLocale(state) },
          payload: generateReplicantChatbotPayload({
            feature: Feature.NOTIFICATION._,
            $subFeature: Feature.NOTIFICATION[`D${day}_REENGAGEMENT`],
          }),
        }),
      });
    },
  ),

  cheat_send_chatbot_video_reengagement_message: action((state, _, api) => {
    const petName = Pet.getters.getPetName(state);
    const species = Pet.getters.getCurrentEvolution(state)!.species;
    api.chatbot.sendMessage(
      api.getUserID(),
      chatbotMessageTemplates.reengagementVideo({
        args: {
          day: '7',
          locale: getLocale(state),
          species,
          petName,
        },
        payload: generateReplicantChatbotPayload({
          feature: Feature.NOTIFICATION._,
          $subFeature: Feature.NOTIFICATION[`D7_REENGAGEMENT`],
        }),
      }),
      { forceChannel: 'chatbot' },
    );
  }),

  cheat_send_chatbot_video_resurrection_message: action(
    (state, { big }: { big: boolean }, api) => {
      const petName = Pet.getters.getPetName(state);
      const species = Pet.getters.getCurrentEvolution(state)!.species;
      api.chatbot.sendMessage(
        api.getUserID(),
        chatbotMessageTemplates.resurrect({
          args: {
            creativeAssetID: big
              ? '2025-02-22-video_save_pet_big'
              : '2025-02-22-video_save_pet',
            locale: getLocale(state),
            species,
            petName,
          },
          payload: generateReplicantChatbotPayload({
            feature: Feature.INVITE._,
            $subFeature: Feature.INVITE.WALLPAPER_REWARD,
          }),
        }),
        { forceChannel: 'chatbot' },
      );
    },
  ),

  cheat_send_chatbot_resurrect_message: action(
    (state, { id }: { id: ResurrectionCreativeVariant }, api) => {
      const petName = Pet.getters.getPetName(state);
      const species = Pet.getters.getCurrentEvolution(state)!.species;
      api.chatbot.sendMessage(
        api.getUserID(),
        chatbotMessageTemplates.resurrect({
          args: {
            creativeAssetID: id,
            locale: getLocale(state),
            species,
            petName,
          },
          payload: generateReplicantChatbotPayload({
            feature: 'resurrect',
            $subFeature: 'oa-any',
          }),
        }),
        { forceChannel: 'chatbot' },
      );
    },
  ),

  cheat_resetPromo: action((state, args: void, api) => {
    resetPromoLaunchLocks(state);
  }),

  cheat_resetResurrection30D: action((state, args: void, api) => {
    state.lastSessionAt = api.date.now() - duration({ days: 31 });
    resetPromoLaunchLocks(state);
  }),

  cheat_resetStarterPackPurchase: action((state, args: void, api) => {
    if (!state.match2.shopBundles) state.match2.shopBundles = {};
    state.match2.shopBundles.starterPackPurchased = false;
    state.match2.shopBundles.petStarterPackPurchased = false;
  }),

  cheat_getAllAccessories: action((state, _: void, api) => {
    const items = getAvailableAccessories();

    for (const item of items) {
      const tiers = Object.keys(tiersAccessoryItemsMap[item.id]);
      const tierID = getRandomItemFromArray(tiers, () => api.math.random());
      addAccessoryItem(state, item.id, tierID, api.date.now());
    }
  }),

  cheat_getOneRandomAccessory: action((state, _: void, api) => {
    const item = getRandomItemFromArray(getAvailableAccessories(), () =>
      api.math.random(),
    );

    const tiers = Object.keys(tiersAccessoryItemsMap[item.id]);
    const tierID = getRandomItemFromArray(tiers, () => api.math.random());
    addAccessoryItem(state, item.id, tierID, api.date.now());
  }),

  cheat_removeAllAccessories: action((state, _: void, api) => {
    state.accessory.purchased = {};
    state.accessory.selected = [];
  }),

  cheat_resetEvent: action((state, _: void, api) => {
    state.competitiveEvent = {};
  }),

  cheat_resetEncyclopedia: action((state, _: void, api) => {
    state.encyclopedia.speciesState = {};
  }),

  cheat_resetTutorialStep: action((state, step: TutorialStep, api) => {
    switch (step) {
      case 'chatWithPets':
        state.tutorial.chatWithPetsShown = false;
        break;
      case 'careForPets':
        state.tutorial.careForPetsShown = false;
        break;
      case 'basicGameplay':
        state.tutorial.basicGameplayShown = false;
        break;
      case 'friendReferral':
        state.tutorial.friendReferralShown = false;
        break;
      case 'profile':
        state.tutorial.profileShown = false;
        break;
      case 'match3':
        state.tutorial.match3Shown = false;
        break;
      case 'visitFriend':
        state.tutorial.visitFriendShown = false;
        break;
      case 'adult':
        state.tutorial.adult = false;
        break;
      case 'duoInvite':
        state.tutorial.duoInvite = false;
        break;
      case 'duoInviteReceived':
        state.tutorial.duoInviteReceived = false;
        break;
      case 'decoration':
        state.tutorial.decoration = false;
        break;
      case 'daycare':
        state.tutorial.daycare = false;
        break;
      case 'decorationBonus':
        state.tutorial.decorationBonus = false;
        break;
      case 'catalog':
        state.tutorial.catalog = false;
        break;
      case 'accessory':
        state.tutorial.accessory = false;
        break;
      case 'encyclopedia':
        state.tutorial.encyclopedia = false;
        break;
      case 'growthCapsule':
        state.tutorial.growthCapsule = false;
        break;
      case 'slots':
        state.tutorial.slots = false;
        break;
      case 'bubbleSlot':
        state.tutorial.bubbleSlot = false;
        break;
      case 'throwBuruBuru':
        state.tutorial.throwBuruBuru = false;
        break;
      case 'match2':
        state.tutorial.match2 = false;
        break;
      case 'decorationProgression':
        state.tutorial.decorationProgression = false;
        break;
      case 'bumbum':
        state.tutorial.bumbum = false;
        break;
      case 'makeHappy':
        state.tutorial.makeHappy = false;
        break;
      case 'addFriends':
        state.tutorial.addFriends = false;
        break;
    }
  }),

  cheat_toggleEditor: action((state, enabled: boolean, api) => {
    state.allowDecorationEditor = enabled;
  }),

  cheat_gotoStage: action((state, stageId: string, api) => {
    state.progression = {
      completedTiers: {},
      currentStage: stageId,
      lastUpdated: api.date.now(),
    };
  }),

  cheat_frenzyReset: action((state, _: void, api) => {
    state.frenzy = {};
  }),

  cheat_setMatch2Level: action((state, level: number) => {
    state.match2.level = level;
  }),

  cheat_setMatch2AddFrenzyProgress: action((state, amount: number, api) => {
    match2FrenzyModifiers.progressFrenzy(state, api.date.now(), amount);
  }),

  cheat_addMatch2ExtraMovesInInventotry: action((state, amount: number) => {
    state.match2.inventory.extraMoves = Math.max(
      0,
      state.match2.inventory.extraMoves + amount,
    );
  }),

  cheat_setMatch2TimedReward: action(
    (state, args: { key: string; timestamp: number }) => {
      setMatch2Timestamp(state, args.key, args.timestamp);
    },
  ),

  cheat_resetTutorials: action((state) => {
    for (const key in state.tutorial) {
      state.tutorial[key as keyof typeof state.tutorial] = false;
    }
  }),

  cheat_simulateResurrection: action((state, _: void, api) => {
    const now = api.date.now();
    const reactivatedAt = now - duration({ days: 31 });

    Object.assign(state as { createdAt: number }, { createdAt: reactivatedAt });
    state.lastSessionAt = reactivatedAt;
    state.previousSessionAt = reactivatedAt;

    const platformStorage = Object.assign({}, state.platformStorage);
    const entry =
      (platformStorage.entry as
        | {
            count?: number;
            lastAdEntry?: number;
          }
        | undefined) ?? {};
    platformStorage.entry = {
      ...entry,
      count: Math.max(entry.count ?? 0, 2),
      first: reactivatedAt,
      last: reactivatedAt,
      lastAdEntry: entry.lastAdEntry ?? 0,
    };
    state.platformStorage = platformStorage;

    if (!state.resurrection) {
      state.resurrection = {};
    }
    state.resurrection.migrationGems = now;
    state.resurrection.hundredGems = now;
  }),

  cheat_simulateNativeInstallReward: action((state) => {
    state.native.appInstallRewardStatus = 'received';
  }),

  cheat_simulateAdminReimbursementMessages: action((state, _: void, api) => {
    state.admin.push({
      coins: 100,
      freeGems: 50,
      match2Consumables: {
        type: 'energy',
        amount: 5,
      },
      reason: 'cheat',
      claimed: false,
      timestamp: api.date.now(),
    });
  }),

  cheat_simulateHomeShortcutLaunchReward: action((state) => {
    delete state.promo.homeShortcutLaunch;
  }),

  cheat_simulateMarketingReward: action((state, _: void, api) => {
    const now = api.date.now();
    Inventory.modifiers.addFreeGems(
      state,
      50,
      { feature: Feature.MARKETING._, $subFeature: CHEAT_MARKETING_EVENT_ID },
      api,
    );
    Inventory.modifiers.addPoints(
      state,
      500,
      { feature: Feature.MARKETING._, $subFeature: CHEAT_MARKETING_EVENT_ID },
      api,
    );

    state.marketing[CHEAT_MARKETING_EVENT_ID] = now;
    delete state.native.payload;
  }),

  cheat_simulateResubscriptionReward: action((state, _: void, api) => {
    state.chatbot.resubscriptionPromptTimestamp = api.date.now();
    state.chatbot.resubscriptionState = 'pending';
  }),

  cheat_simulateGuestLineLoginReward: action((state, _: void, api) => {
    grantMatch2Rewards(
      state,
      [guestLineLoginReward],
      { feature: 'guest_to_line' },
      api,
    );

    delete state.native.payload;
  }),

  cheat_simulateInviteReferralReward: action((state, _: void, api) => {
    const now = api.date.now();
    delete state.promo.inGameFriendReferralRewardsCollection;

    state.social.referral.pendingRewards.push({
      friendId: `cheat_referral_${now}`,
      reward: {
        type: 'gems',
        value: successInviteRewardGems,
      },
      createdAt: now,
    });
  }),

  cheat_addTeamEventScore: action((state, args: { score: number }, rawApi) => {
    const api = getSharedStateActionsAPI(rawApi);
    const currentTeamId = state.team.currentTeamId;
    if (!currentTeamId) return;

    api.sharedStates.team.postMessage.postEventScore(currentTeamId, {
      timestamp: api.date.now(),
      score: args.score,
    });
  }),

  cheat_resetAds: action((state, args: void, api) => {
    state.seenAdTimestamps = [];
  }),

  cheat_winCurrentRace: action((state, _: void, api) => {
    const now = api.date.now();
    const activeRace = getActiveRace(now);
    if (!activeRace) return;

    const { id } = activeRace;

    if (!state.match2.races[id]?.length) {
      state.match2.races[id] = [
        { id, score: 0, createdAt: now, modifiedAt: now, bots: [] },
      ];
    }

    const raceIndex = state.match2.races[id].length - 1;
    const race = state.match2.races[id][raceIndex];

    race.score = 9999;
    race.createdAt = now - duration({ hours: 48 });
    race.modifiedAt = now;
  }),

  cheat_resetRaces: action((state) => {
    state.match2.races = {};
    state.match2.pendingRaceScore = undefined;
    state.match2.lastViewedRaceScore = undefined;
  }),

  cheat_makePetHappy: action((state, args: void, api) => {
    Pet.modifiers.makePetHappy(state, api.date.now());
  }),

  cheat_resetMakeHappyCooldown: action((state) => {
    setMatch2Timestamp(state, getMakeHappyCooldownTimestampKey(), 0);
  }),

  cheat_resetAgeConsent: action((state, value: boolean, api) => {
    state.ageConsentJPAccepted = value;
  }),

  /**
   * Send a greeting message to the user
   */
  cheat_sendGreetingMessage: action(
    (_, period: 'morning' | 'lunch' | 'evening', api) => {
      api.scheduledActions.schedule.sendPetChatMessage({
        args: period,
        notificationId: 'sendPetChatMessageCheat',
        delayInMS: 0,
      });
    },
  ),

  cheat_pasteState: action((state, playerDataStr: string, api) => {
    if (!playerDataStr) return;
    const playerData = JSON.parse(playerDataStr);
    const playerState = playerData.state;
    if (!playerState) return;
    console.log('Pasting state:', playerState);
    for (const key in playerState) {
      (state as Record<string, unknown>)[key] = playerState[key];
    }
  }),

  cheat_sessionEndForTests: action((state, args: void, api) => {
    const now = api.date.now();
    updateRaceBotsOnGameEnd(state, now);

    // Auto spawn feature
    const currentSpecies = Pet.getters.getEquippedPetSpecies(state);

    // if no pet, create first pet automatically
    if (!currentSpecies) {
      Pet.modifiers.createFirstPet(state, now, () => api.math.random());
    }
  }),

  cheat_sendDevChatbotURL: action(
    (state, args: { url: string; message: string; cta: string }, api) => {
      const template = renderTemplatesWithAssets(
        {},
        {
          dev: renderTemplate({
            args: SB.object({}),
            renderers: {
              line: () => {
                return {
                  type: 'flex',
                  altText: args.message,
                  contents: {
                    type: 'bubble',
                    body: {
                      type: 'box',
                      layout: 'vertical',
                      contents: [
                        {
                          type: 'text',
                          text: args.message,
                          weight: 'bold',
                          size: 'xl',
                        },
                        {
                          type: 'box',
                          layout: 'vertical',
                          margin: 'lg',
                          spacing: 'sm',
                          contents: [],
                        },
                      ],
                    },
                    footer: {
                      type: 'box',
                      layout: 'vertical',
                      spacing: 'sm',
                      contents: [
                        {
                          type: 'button',
                          style: 'link',
                          height: 'sm',
                          action: {
                            type: 'uri',
                            label: args.cta,
                            uri: args.url,
                          },
                        },
                        {
                          type: 'box',
                          layout: 'vertical',
                          contents: [],
                          margin: 'sm',
                        },
                      ],
                      flex: 0,
                    },
                  },
                  sender: {
                    name: 'Dev',
                    iconUrl: '',
                  },
                };
              },
            },
          }),
        },
      );

      deliverChatbotMessage(state, api, {
        receiverId: api.getUserID(),
        channel: 'oa',
        message: template.dev({
          args: {},
          payload: {
            $channel: 'CHATBOT',
            feature: 'DEV',
            $subFeature: 'DEV_CHATBOT_LINK',
          },
        }),
      });
    },
  ),

  cheat_addJankenPendingResult: action(
    (
      state,
      _args: void,
      api,
    ): {
      challengeId: string;
      receiverId: string;
      opponentSpecies: string;
      opponentDisplayName: string;
    } => {
      const now = api.date.now();
      const randomInt = Math.floor(api.math.random() * 36 ** 8);
      const random = randomInt.toString(36).padStart(8, '0');
      const challengeId = `jnk_${now}_${random}`;
      const receiverId = `cheat_receiver_${random}`;
      const senderId = state.id;

      const cheatNames = [
        'Alex',
        'たなかひろし',
        'Alexandrovichenko',
        'たなかひろしくんさま',
        'Kim',
        'さくら',
        'Bob',
        'やまだたろう',
      ];
      const opponentDisplayName =
        cheatNames[Math.floor(api.math.random() * cheatNames.length)];
      const opponentSpecies =
        petSpeciesTuple[Math.floor(api.math.random() * petSpeciesTuple.length)];

      // Create pending result directly (postMessage to self doesn't work
      // because friendId becomes state.id instead of the cheat receiverId)
      createJankenPendingResult(state, {
        challengeId,
        receiverId,
        createdAt: now,
        opponentSpecies,
        opponentDisplayName,
      });

      const { result, senderMove, receiverMove } = deriveJankenOutcome(
        challengeId,
        senderId,
        receiverId,
      );
      api.scheduledActions.schedule.jankenNotifyResult({
        args: {
          challengeId,
          receiverId,
          outcome: result.sender,
          opponentName: opponentDisplayName,
          senderMove,
          receiverMove,
          opponentSpecies,
        },
        delayInMS: 0,
        targetUserId: senderId,
        notificationId: `jankenNotifyResult_${challengeId}_${receiverId}`,
      });

      return { challengeId, receiverId, opponentSpecies, opponentDisplayName };
    },
  ),

  cheat_resetJankenDailyRewards: action((state) => {
    if (!state.janken) return;
    state.janken.dailyRewards = 0;
    state.janken.dailyStamp = undefined;
  }),

  cheat_resetJankenTutorial: action((state) => {
    if (!state.janken) return;
    state.janken.tutorialSeenAt = undefined;
  }),

  cheat_sendJankenResultOA: action((state, _args: void, api) => {
    const r = api.math.random;

    // Random outcome
    const outcome = jankenOutcomes[Math.floor(r() * jankenOutcomes.length)];

    // Random sender move, then derive receiver move to match outcome
    const senderMove = jankenMoves[Math.floor(r() * jankenMoves.length)];
    const beats = {
      rock: 'scissors',
      scissors: 'paper',
      paper: 'rock',
    } as const;
    const losesTo = {
      rock: 'paper',
      paper: 'scissors',
      scissors: 'rock',
    } as const;
    const receiverMove =
      outcome === 'win'
        ? beats[senderMove]
        : outcome === 'lose'
          ? losesTo[senderMove]
          : senderMove;

    // Random opponent species (sender species comes from state in the handler)
    const opponentSpecies =
      petSpeciesTuple[Math.floor(r() * petSpeciesTuple.length)];

    const opponentName = state.publicProfile?.name ?? 'Test Friend';

    api.scheduledActions.schedule.jankenNotifyResult({
      args: {
        challengeId: `jnk_${api.date.now()}_cheatoa`,
        receiverId: `cheat_receiver_${api.date.now()}`,
        outcome,
        opponentName,
        senderMove,
        receiverMove,
        opponentSpecies,
      },
      delayInMS: 0,
      targetUserId: state.id,
      notificationId: `jankenResultOA_cheat_${api.date.now()}`,
    });

    console.debug(
      `Sent Janken Result OA: ${outcome}, ${opponentSpecies}, ${senderMove} vs ${receiverMove}`,
    );
  }),

  ...decorationCheats,
  ...friendsCheats,
});
